// Upgrades for projects saved by earlier WebCut versions that need the media
// loaded first (pure-JSON fixes live in normalizeProject).
import { Project, Clip, newTrack, TRACK_PREFIX } from "../state/model";
import { uid } from "../util";

/** The first WebCut builds kept a video's sound as a buffer on the video asset
 *  itself (no separate audio clips). Current versions split it into a hidden
 *  child audio asset placed as linked audio clips. Convert such projects so they
 *  keep their sound: audio-track clips that pointed at the video are re-pointed;
 *  video clips with no audio partner get a linked audio clip on an audio track.
 *  Returns how many video assets were upgraded. */
export function upgradeLegacyVideoAudio(p: Project, buffers: Map<string, AudioBuffer>): number {
  let upgraded = 0;
  for (const v of p.assets) {
    if (v.kind !== "video" || v.generator || v.audioAssetIds?.length) continue;
    const buf = buffers.get(v.id);
    if (!buf) continue;
    const child = {
      id: uid("ast"), name: `${v.name} (audio)`, kind: "audio" as const, ownerId: v.id,
      sampleRate: buf.sampleRate, channels: buf.numberOfChannels, length: buf.length, duration: buf.duration,
    };
    p.assets.push(child);
    buffers.set(child.id, buf);
    buffers.delete(v.id); // video assets carry no audio buffer any more
    v.audioAssetIds = [child.id];
    v.hasAudio = true;
    upgraded++;

    const audioRefs = p.tracks.filter((t) => t.kind !== "video").flatMap((t) => t.clips.filter((c) => c.assetId === v.id));
    if (audioRefs.length) { for (const c of audioRefs) c.assetId = child.id; continue; }
    // Give each video clip a linked audio clip, on the first audio track free there.
    for (const vt of p.tracks.filter((t) => t.kind === "video")) {
      for (const vc of vt.clips) {
        if (vc.assetId !== v.id) continue;
        const linkId = vc.linkId ?? uid("lnk");
        vc.linkId = linkId;
        const ac: Clip = { ...vc, id: uid("clip"), assetId: child.id, name: child.name, linkId, transform: undefined, crop: undefined, transitionIn: undefined };
        const free = (t: Project["tracks"][number]) => !t.clips.some((c) => c.start < ac.start + ac.duration && c.start + c.duration > ac.start);
        let at = p.tracks.find((t) => t.kind === "audio" && free(t));
        if (!at) {
          const n = p.tracks.filter((t) => t.kind === "audio").length + 1;
          at = newTrack(p.tracks.length, "audio");
          at.name = `${TRACK_PREFIX.audio} ${n}`;
          p.tracks.push(at);
        }
        at.clips.push(ac);
      }
    }
  }
  return upgraded;
}
